import React, { useState, useEffect } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Resize,
  Sort,
  ContextMenu,
  Filter,
  Page,
  ExcelExport,
  PdfExport,
  Inject,
} from "@syncfusion/ej2-react-grids";
import { Header } from "../Components";
import axios from "axios";
import { FaSearch, FaTrash, FaFileExcel, FaFilePdf } from "react-icons/fa";
import { useBaseUrl } from "../Contexts/BaseUrlContext";
import { useStateContext } from "../Contexts/ContextProvider";

// Grid column configuration
const inquiryGrid = [
  {
    field: "id",
    headerText: "ID",
    width: 80,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">ID</span>,
  },
  {
    field: "name",
    headerText: "Name",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Name</span>,
  },
  {
    field: "course",
    headerText: "Course",
    width: 150,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Course</span>,
  },
  {
    field: "contact_number",
    headerText: "Contact Number",
    width: 120,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Contact</span>,
  },
  {
    field: "email_id",
    headerText: "Email",
    width: 150,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Email</span>,
  },
  {
    field: "city",
    headerText: "City",
    width: 100,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">City</span>,
  },
  {
    field: "message",
    headerText: "Message",
    width: 200,
    textAlign: "Left",
    headerTemplate: () => <span className="font-bold">Message</span>,
  },
  {
    field: "date_time",
    headerText: "Date & Time",
    width: 150,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Date & Time</span>,
  },
  {
    field: "Actions",
    headerText: "Actions",
    width: 100,
    textAlign: "Center",
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex justify-center">
        <button
          className="text-red-600 hover:text-red-800 transition-transform transform hover:scale-110 focus:outline-none"
          onClick={() => props.deleteHandler(props.id)}
          title="Delete"
        >
          <FaTrash size={18} />
        </button>
      </div>
    ),
  },
];

const Inquiry = () => {
  const [inquiryData, setInquiryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { baseUrl } = useBaseUrl();

  // Fetch inquiry data from API
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const formData = new FormData();
        formData.append("action", "get-inquiries");

        const response = await axios.post(
          `${baseUrl}inquiry_form.php`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              // 'Accept': 'application/json',
            },
            withCredentials: true,
          }
        );
        console.log("API Response:", response.data);
        if (response.data.status) {
          setInquiryData(response.data.data || []);
        } else {
          setErrorMessage(response.data.error || "Failed to fetch inquiries.");
        }
      } catch (error) {
        console.error("Axios Error:", error.response || error);
        setErrorMessage(
          "Error connecting to the server. Please check your network or API configuration."
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Handle delete action
  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this inquiry?")) {
      try {
        const data = new FormData();
        data.append("action", "delete-inquiry");
        data.append("id", id);

        const response = await axios.post(`${baseUrl}inquiry_form.php`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            // 'Accept': 'application/json',
          },
          withCredentials: true,
        });
        console.log("Delete Response:", response.data);
        if (response.data.status) {
          setInquiryData(inquiryData.filter((item) => item.id !== id));
          alert("Inquiry deleted successfully.");
        } else {
          alert(
            "Failed to delete inquiry: " +
              (response.data.error || "Unknown error")
          );
        }
      } catch (error) {
        console.error("Error deleting inquiry:", error.response || error);
        alert("Failed to delete inquiry.");
      }
    }
  };

  // Search filter
  const filteredData = inquiryData.filter(
    (item) =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.course?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.contact_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.email_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Inject handlers into grid data
  const gridData = filteredData.map((item) => ({
    ...item,
    deleteHandler,
  }));

  const { setCurrentColor, setCurrentMode, currentMode } = useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }
  }, []);

  return (
    <div
      style={currentMode === "Dark" ? { backgroundColor: "#20232A" } : {}}
      className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn bg-gray-100 min-h-screen"
    >
      <Header category="Dashboard" title="Inquiry Management" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* Search and Controls */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, course, contact, email, city, or message..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() =>
              document
                .getElementById("inquiryGrid")
                .ej2_instances[0].excelExport()
            }
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() =>
              document
                .getElementById("inquiryGrid")
                .ej2_instances[0].pdfExport()
            }
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="inquiryGrid"
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          allowExcelExport={true}
          allowPdfExport={true}
          allowFiltering={true}
          allowResizing={true}
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: "Menu", showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {inquiryGrid.map((item, index) => (
              <ColumnDirective key={index} {...item} />
            ))}
          </ColumnsDirective>
          <Inject
            services={[
              Resize,
              Sort,
              ContextMenu,
              Filter,
              Page,
              ExcelExport,
              PdfExport,
            ]}
          />
        </GridComponent>
      </div>
    </div>
  );
};

export default Inquiry;
